import type { NextRequest } from 'next/server'

import { ROUTES } from '@/constants'

import { fetchPostHogFlagVariant } from './posthogFlags'

export const LANDING_EXPERIMENT_KEY = 'ab_landing_create_page_vs_homepage'
export const MYQRCODE_DESKTOP_LANDING_EXPERIMENT_KEY =
	'ab_myqrcode_004_lander_desktop_homepage'
export const MYQRCODE_MOBILE_LANDING_EXPERIMENT_KEY =
	'ab_myqrcode_005_lander_mobile_create'
export const LANDING_EXPERIMENT_TEST_VARIANT = 'test'
export const LANDING_EXPERIMENT_FLAG_COOKIE = 'ph_landing_flag'
export const LANDING_EXPERIMENT_VARIANT_COOKIE = 'ph_landing_variant'
export const LANDING_EXPERIMENT_ID_COOKIE = 'ph_landing_distinct_id'
export const LANDING_EXPERIMENT_COOKIE_MAX_AGE = 600
export const LANDING_EXPERIMENT_VISIT_COOKIE = 'qr_landing_visit'
export const LANDING_EXPERIMENT_VISIT_MAX_AGE = 1800

export const isGooglePrefetchProxyRequest = (request: NextRequest) =>
	!!request.headers.get('sec-purpose')?.includes('anonymous-client-ip')

export type LandingExperiment = {
	flagKey: string
	testPath: string
}

export const resolveLandingExperiment = ({
	isClone1,
	deviceType,
	pathnameWithoutLocale,
}: {
	isClone1: boolean
	deviceType: string
	pathnameWithoutLocale: string
}): LandingExperiment | null => {
	if (isClone1) {
		return pathnameWithoutLocale === ROUTES.index
			? { flagKey: LANDING_EXPERIMENT_KEY, testPath: ROUTES.altLanding }
			: null
	}

	if (deviceType === 'mobile') {
		return pathnameWithoutLocale === ROUTES.index
			? {
					flagKey: MYQRCODE_MOBILE_LANDING_EXPERIMENT_KEY,
					testPath: ROUTES.altLanding,
				}
			: null
	}

	return pathnameWithoutLocale === ROUTES.altLanding
		? {
				flagKey: MYQRCODE_DESKTOP_LANDING_EXPERIMENT_KEY,
				testPath: ROUTES.index,
			}
		: null
}

export const getPostHogPersistenceCookieName = (token: string) =>
	`ph_${token.replace(/\+/g, 'PL').replace(/\//g, 'SL').replace(/=/g, 'EQ')}_posthog`

export const readPostHogDistinctId = (
	rawCookie: string | undefined,
):
	| { status: 'absent' }
	| { status: 'invalid' }
	| { status: 'found'; id: string } => {
	if (!rawCookie) return { status: 'absent' }
	try {
		const parsed = JSON.parse(decodeURIComponent(rawCookie))
		const id = parsed?.distinct_id
		return typeof id === 'string' && id
			? { status: 'found', id }
			: { status: 'invalid' }
	} catch {
		return { status: 'invalid' }
	}
}

export const generateUuidV7 = (): string => {
	const bytes = crypto.getRandomValues(new Uint8Array(16))
	const timestamp = BigInt(Date.now())
	for (let i = 0; i < 6; i++) {
		bytes[i] = Number((timestamp >> BigInt(8 * (5 - i))) & BigInt(0xff))
	}
	bytes[6] = (bytes[6]! & 0x0f) | 0x70
	bytes[8] = (bytes[8]! & 0x3f) | 0x80
	const hex = Array.from(bytes, (byte) =>
		byte.toString(16).padStart(2, '0'),
	).join('')
	return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

export const fetchLandingExperimentVariant = (
	apiKey: string,
	distinctId: string,
	flagKey: string,
): Promise<string | null> =>
	fetchPostHogFlagVariant({ apiKey, distinctId, flagKey })
